import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import ProjectLayoutService from 'service/ClientTransmoraService/ProjectLayoutService';
import {
  DashboardDimensionItem,
  DashboardMetricItem,
} from 'domain/payload/response/clientTransmora/projectLayout/ProjectDashboardConfigResp';
import {
  DashboardGroup,
  ChartWidget,
  parseDashboardJson,
  serializeDashboardGroups,
  getDefaultSampleDashboardGroups,
} from './dashboardSerializer';
import DashboardCanvasPanel from './DashboardCanvasPanel';
import DashboardOptionsPanel from './DashboardOptionsPanel';
import DashboardWidgetConfigModal from './DashboardWidgetConfigModal';
import '../templateBuilder/templateBuilder.css';

interface DashboardTemplateBuilderProps {
  value: string;
  onChange: (jsonStr: string) => void;
  projectId: string;
}

const DashboardTemplateBuilder: React.FC<DashboardTemplateBuilderProps> = ({
  value,
  onChange,
  projectId,
}) => {
  const { profile } = useSelector((state: any) => state.auth);
  const projectLayoutService = ProjectLayoutService();

  const [mode, setMode] = useState<'visual' | 'code'>('visual');
  const [groups, setGroups] = useState<DashboardGroup[]>([]);
  const [rawJson, setRawJson] = useState<string>('');

  // Dashboard Config options
  const [dimensions, setDimensions] = useState<DashboardDimensionItem[]>([]);
  const [metrics, setMetrics] = useState<DashboardMetricItem[]>([]);
  const [isLoadingConfig, setIsLoadingConfig] = useState<boolean>(false);

  // Modal State
  const [editingWidget, setEditingWidget] = useState<{
    widget: ChartWidget;
    groupId: string;
  } | null>(null);

  // Fetch Dashboard Config when projectId changes
  useEffect(() => {
    const fetchConfig = async () => {
      const token = profile?.data.token;
      if (!token || !projectId) {
        setDimensions([]);
        setMetrics([]);
        return;
      }
      setIsLoadingConfig(true);
      try {
        const res = await projectLayoutService.DashboardConfig(token, projectId);
        if (res.data) {
          setDimensions(res.data.dimensions || []);
          setMetrics(res.data.metrics || []);
        }
      } catch (err) {
        console.error('Failed to fetch dashboard config:', err);
      } finally {
        setIsLoadingConfig(false);
      }
    };
    fetchConfig();
  }, [projectId]);

  // Sync internal groups state from value prop on mount / value update
  useEffect(() => {
    setRawJson(value || '[]');
    const parsed = parseDashboardJson(value);
    setGroups(parsed);
  }, [value]);

  // Handle Groups update from Visual Canvas
  const handleUpdateGroups = (newGroups: DashboardGroup[]) => {
    setGroups(newGroups);
    const jsonStr = serializeDashboardGroups(newGroups);
    setRawJson(jsonStr);
    onChange(jsonStr);
  };

  // Handle Raw JSON Change in Code Mode
  const handleRawJsonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setRawJson(val);
    onChange(val);
    const parsed = parseDashboardJson(val);
    setGroups(parsed);
  };

  // Add Chart Widget to Group
  const handleAddWidgetToGroup = (groupId: string, chartType: string = 'bar') => {
    const newWidget: ChartWidget = {
      id: `widget-${Date.now()}`,
      chartName: 'New Chart Widget',
      chartType: chartType as any,
      colSpan: 3,
    };

    handleUpdateGroups(
      groups.map((g) => {
        if (g.id === groupId) {
          return {
            ...g,
            group: [...g.group, newWidget],
          };
        }
        return g;
      })
    );

    // Automatically open edit modal for newly created widget
    setEditingWidget({ widget: newWidget, groupId });
  };

  // Quick Add Widget from Right Options Panel
  const handleAddQuickWidget = (chartType: string) => {
    let targetGroupId = groups.length > 0 ? groups[groups.length - 1].id : null;
    let currentGroups = [...groups];

    if (!targetGroupId) {
      const newGroup: DashboardGroup = {
        id: `group-${Date.now()}`,
        group: [],
      };
      currentGroups.push(newGroup);
      targetGroupId = newGroup.id;
    }

    const newWidget: ChartWidget = {
      id: `widget-${Date.now()}`,
      chartName:
        chartType === 'heatmap'
          ? 'Metric Heatmap'
          : chartType === 'pie'
          ? 'Global Distribution'
          : 'Breakdown Chart',
      chartType: chartType as any,
      colSpan: 3,
    };

    const updatedGroups = currentGroups.map((g) => {
      if (g.id === targetGroupId) {
        return {
          ...g,
          group: [...g.group, newWidget],
        };
      }
      return g;
    });

    handleUpdateGroups(updatedGroups);
  };

  // Load Preset Sample Dashboard
  const handleLoadSample = () => {
    const sample = getDefaultSampleDashboardGroups(dimensions, metrics);
    handleUpdateGroups(sample);
  };

  // Handle Save Widget from Modal
  const handleSaveModalWidget = (updatedWidget: ChartWidget) => {
    if (!editingWidget) return;
    const { groupId } = editingWidget;

    handleUpdateGroups(
      groups.map((g) => {
        if (g.id === groupId) {
          return {
            ...g,
            group: g.group.map((w) => (w.id === updatedWidget.id ? updatedWidget : w)),
          };
        }
        return g;
      })
    );

    setEditingWidget(null);
  };

  return (
    <div className="w-100 mb-3 border rounded-3 bg-white p-3 shadow-xs">
      {/* BUILDER HEADER TOOLBAR */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 pb-3 border-bottom gap-2">
        <div>
          <h6 className="fw-bold mb-1 text-dark d-flex align-items-center">
            <i className="fas fa-chart-line text-primary me-2"></i> Dashboard Template Builder
          </h6>
        </div>

        {/* MODE TOGGLE */}
        <div className="d-flex align-items-center gap-2">
          <div className="btn-group btn-group-sm" role="group">
            <button
              type="button"
              className={`btn ${mode === 'visual' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setMode('visual')}
            >
              <i className="fas fa-paint-brush me-1"></i> Visual Canvas
            </button>
            <button
              type="button"
              className={`btn ${mode === 'code' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setMode('code')}
            >
              <i className="fas fa-code me-1"></i> JSON Code
            </button>
          </div>
        </div>
      </div>

      {/* MODE 1: VISUAL CANVAS SPLIT VIEW */}
      {mode === 'visual' && (
        <div className="row g-3">
          {/* LEFT 50% SPLIT: DASHBOARD CANVAS PANEL */}
          <div className="col-12 col-lg-6">
            <DashboardCanvasPanel
              groups={groups}
              dimensions={dimensions}
              metrics={metrics}
              onUpdateGroups={handleUpdateGroups}
              onEditWidget={(widget, groupId) => setEditingWidget({ widget, groupId })}
              onAddWidgetToGroup={handleAddWidgetToGroup}
              onLoadSample={handleLoadSample}
            />
          </div>

          {/* RIGHT 50% SPLIT: DASHBOARD OPTIONS PANEL */}
          <div className="col-12 col-lg-6">
            <DashboardOptionsPanel
              onAddQuickWidget={handleAddQuickWidget}
            />
          </div>
        </div>
      )}

      {/* MODE 2: RAW JSON CODE VIEW */}
      {mode === 'code' && (
        <div className="p-3 bg-light rounded-3 border">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <label className="form-label fw-bold text-dark mb-0 small">
              Raw Dashboard Template JSON
            </label>
            <span className="badge bg-secondary" style={{ fontSize: '10px' }}>
              Auto-synced with Visual Canvas
            </span>
          </div>
          <textarea
            className="form-control font-monospace text-dark"
            style={{ fontSize: '13px', lineHeight: '1.5', minHeight: '480px', resize: 'vertical' }}
            value={rawJson}
            onChange={handleRawJsonChange}
            placeholder="[]"
          />
        </div>
      )}

      {/* WIDGET SETTINGS MODAL */}
      <DashboardWidgetConfigModal
        show={!!editingWidget}
        widget={editingWidget ? editingWidget.widget : null}
        dimensions={dimensions}
        metrics={metrics}
        onSave={handleSaveModalWidget}
        onClose={() => setEditingWidget(null)}
      />
    </div>
  );
};

export default DashboardTemplateBuilder;
